import type { SessionInfo } from '../types/analytics';

export interface SessionTrackerDeps {
  generateId?: () => string;
  now?: () => number;
  storage?: { getItem(key: string): string | null; setItem(key: string, value: string): void };
  navigator?: { userAgent: string };
  screen?: { width: number; height: number };
  document?: { referrer: string };
  setInterval?: (fn: () => void, ms: number) => number;
  clearInterval?: (id: number) => void;
}

const RETURNING_USER_KEY = 'aliza_analytics_returning';

export class SessionTracker {
  private sessionId: string;
  private startedAt: number;
  private deps: SessionTrackerDeps;
  private heartbeatId: number | null = null;
  private heartbeatCallback: (() => void) | null = null;
  private isReturning: boolean;

  constructor(deps?: SessionTrackerDeps) {
    this.deps = deps ?? {};
    const now = this.deps.now?.() ?? Date.now();
    this.sessionId = this.deps.generateId?.() ?? this.fallbackId();
    this.startedAt = now;
    this.isReturning = this.checkReturningUser();
    this.markReturningUser();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getSessionInfo(): SessionInfo {
    return {
      sessionId: this.sessionId,
      startedAt: this.startedAt,
      deviceType: this.detectDeviceType(),
      screenSize: this.getScreenSize(),
      userAgent: this.deps.navigator?.userAgent ?? '',
      referrer: this.deps.document?.referrer ?? '',
    };
  }

  getSessionDuration(): number {
    const now = this.deps.now?.() ?? Date.now();
    return now - this.startedAt;
  }

  isReturningUser(): boolean {
    return this.isReturning;
  }

  startHeartbeat(intervalMs: number, callback: () => void): void {
    this.stopHeartbeat();
    this.heartbeatCallback = callback;
    const setIntervalFn = this.deps.setInterval ?? (typeof globalThis !== 'undefined' ? globalThis.setInterval : undefined);
    if (!setIntervalFn) return;
    this.heartbeatId = setIntervalFn(() => callback(), intervalMs) as unknown as number;
  }

  stopHeartbeat(): void {
    if (this.heartbeatId === null) return;
    const clearIntervalFn = this.deps.clearInterval ?? (typeof globalThis !== 'undefined' ? globalThis.clearInterval : undefined);
    clearIntervalFn?.(this.heartbeatId);
    this.heartbeatId = null;
    this.heartbeatCallback = null;
  }

  destroy(): void {
    this.stopHeartbeat();
  }

  private checkReturningUser(): boolean {
    return this.deps.storage?.getItem(RETURNING_USER_KEY) === 'true';
  }

  private markReturningUser(): void {
    this.deps.storage?.setItem(RETURNING_USER_KEY, 'true');
  }

  private detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const ua = this.deps.navigator?.userAgent ?? '';
    if (/mobile/i.test(ua)) return 'mobile';
    if (/tablet|ipad/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  private getScreenSize(): { width: number; height: number } {
    return {
      width: this.deps.screen?.width ?? 0,
      height: this.deps.screen?.height ?? 0,
    };
  }

  private fallbackId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
